import axios, { AxiosRequestConfig, Method } from "axios";
import router from "@/router";
import { ElMessage } from "element-plus";
import {
  getLanguage,
  getLoginUid,
  getSessionSecretKey
} from "@/utils/cookies";
import { resetAuthState } from "@/utils/reset-auth";
import {
  computeFileMd5Hex,
  extractFileFromFormData,
  FILE_HASH_HEADER,
  isFormDataUpload
} from "@/utils/file-hash";
import { applyRequestSign, extractBusinessResponseMeta, isSignatureErrorStatus, resolveSignatureErrorMessage, sanitizeRequestParams, shouldSkipSign } from "@/utils/request-sign";
import { getServerTimestamp } from "@/utils/server-time";

/** 鉴权失败时不跳转登录页的接口（登录流程内的辅助请求） */
const AUTH_REDIRECT_SKIP_URLS = [
  "/api/admin/permission/find/adminId",
  "/auth/open/login"
];

function shouldSkipAuthRedirect(url?: string) {
  if (!url) return false;
  return AUTH_REDIRECT_SKIP_URLS.some(item => url.includes(item));
}

const redirectToLogin = () => {
  resetAuthState();
  router.replace({ path: "/login" });
};

/**
 * token 失效时多个接口并发会触发多次提示，
 * 这里做一个全局节流：在同一段时间内只提示一次。
 */
const AUTH_FAIL_TOAST_THROTTLE_MS = 5000;
const AUTH_FAIL_TOAST_LAST_TIME_KEY = "auth_fail_toast_last_time";
function showAuthFailToastOnce(message: string | undefined, status: number) {
  const now = Date.now();
  const last = Number(
    sessionStorage.getItem(AUTH_FAIL_TOAST_LAST_TIME_KEY) ?? 0
  );
  if (now - last < AUTH_FAIL_TOAST_THROTTLE_MS) return;

  sessionStorage.setItem(AUTH_FAIL_TOAST_LAST_TIME_KEY, String(now));
  ElMessage.error(message || `登录失效(status=${status})`);
}

/**
 * 请求失败后的错误统一处理
 * @param {Number} status 请求失败的状态码
 */
const errorHandle = (status: number, other: string) => {
  switch (status) {
    case 302:
      ElMessage.error("接口重定向了！");
      break;
    case 400:
      ElMessage.error(
        other || "发出的请求有错误，服务器没有进行新建或修改数据的操作"
      );
      break;
    case 401:
      showAuthFailToastOnce(other, status);
      redirectToLogin();
      break;
    case 403:
      showAuthFailToastOnce(
        other || "登录过期,用户得到授权，但是访问是被禁止的==>" + status,
        status
      );
      setTimeout(() => {
        redirectToLogin();
      }, 1000);
      break;
    case 404:
      ElMessage.error("网络请求不存在==>" + status);
      break;
    case 406:
      ElMessage.error("请求的格式不可得==>" + status);
      break;
    case 408:
      ElMessage.error(" 请求超时！");
      break;
    case 410:
      ElMessage.error("请求的资源被永久删除，且不会再得到的==>" + status);
      break;
    case 422:
      ElMessage.error("当创建一个对象时，发生一个验证错误==>" + status);
      break;
    case 500:
      ElMessage.error("服务器发生错误，请检查服务器==>" + status);
      break;
    case 502:
      ElMessage.error("网关错误==>" + status);
      break;
    case 503:
      ElMessage.error("服务不可用，服务器暂时过载或维护==>" + status);
      break;
    case 504:
      ElMessage.error("网关超时==>" + status);
      break;
    default:
      ElMessage.error("其他错误错误==>" + status);
  }
};

interface PendingType {
  url?: string;
  method?: Method;
  params: any;
  data: any;
  cancel: any;
}
const pending: Array<PendingType> = [];
const CancelToken = axios.CancelToken;

const removePending = (config: AxiosRequestConfig) => {
  // FormData 无法 JSON 序列化比对，且上传不宜被「重复请求取消」误伤
  if (isFormDataUpload(config.data)) {
    return;
  }
  for (const key in pending) {
    const item: number = +key;
    const list: PendingType = pending[key];
    if (
      list.url === config.url &&
      list.method === config.method &&
      JSON.stringify(list.params) === JSON.stringify(config.params) &&
      JSON.stringify(list.data) === JSON.stringify(config.data)
    ) {
      list.cancel("操作太频繁，请稍后再试");
      pending.splice(item, 1);
    }
  }
};

function parseResponseData(data: string) {
  if (data === "" || data === null || data === undefined) {
    return null;
  }
  if (typeof data !== "string") {
    return data;
  }
  try {
    return JSON.parse(data);
  } catch {
    return data;
  }
}

const instance = axios.create({
  headers: {
    "Content-Type": "application/json;charset=UTF-8",
    lang: getLanguage()
  },
  timeout: 1000 * 30,
  baseURL: process.env.VUE_APP_BASE_API,
  withCredentials: false,
  transformResponse: [parseResponseData]
});

function shouldRetryRequest(
  config: AxiosRequestConfig | undefined,
  error: { response?: { status?: number } }
): boolean {
  if (!config || isFormDataUpload(config.data)) {
    return false;
  }
  const status = error.response?.status;
  // 无 response 多为网络中断；业务 4xx/5xx 不重试，避免验签失败等被连打
  if (!status) {
    return false;
  }
  return status === 502 || status === 503 || status === 504;
}

function handleSignatureError(status: number, message?: string | null) {
  ElMessage.error(resolveSignatureErrorMessage(status, message));
}

instance.interceptors.request.use(
  async config => {
    removePending(config);
    if (!isFormDataUpload(config.data)) {
      config.cancelToken = new CancelToken(c => {
        pending.push({
          url: config.url,
          method: config.method,
          params: config.params,
          data: config.data,
          cancel: c
        });
      });
    }
    const token = localStorage.getItem("token");
    const tokensession = localStorage.getItem("tokensession");
    if (token) {
      config.headers["pc-access-token"] = token;
      if (tokensession) {
        config.headers["manager-web-session"] = tokensession;
      }
    }

    const requestUrl = config.url || "";
    const sessionSecretKey = getSessionSecretKey();
    const loginUid = getLoginUid();

    let fileHash: string | undefined;
    if (isFormDataUpload(config.data)) {
      delete config.headers["Content-Type"];
      const file = extractFileFromFormData(config.data);
      if (!file) {
        ElMessage.error("上传文件无效，请重新选择文件后再提交");
        return Promise.reject(new Error("上传文件无效"));
      }
      try {
        fileHash = await computeFileMd5Hex(file);
        config.headers[FILE_HASH_HEADER] = fileHash;
      } catch {
        ElMessage.error("文件校验失败，请重试");
        return Promise.reject(new Error("文件哈希计算失败"));
      }
    }

    if (
      token &&
      sessionSecretKey &&
      loginUid &&
      !shouldSkipSign(requestUrl)
    ) {
      const timestamp = String(getServerTimestamp());
      const signParams = sanitizeRequestParams(
        config.params as Record<string, unknown> | undefined
      );
      config.params = signParams;

      if (isFormDataUpload(config.data) && !fileHash) {
        ElMessage.error("上传签名校验失败，请重新选择文件");
        return Promise.reject(new Error("缺少文件哈希"));
      }

      const { bodyStr, nonce, sign } = applyRequestSign({
        uid: loginUid,
        token,
        sessionKeyBase64: sessionSecretKey,
        timestamp,
        params: signParams,
        data: config.data,
        fileHash
      });
      if (
        config.data !== undefined &&
        config.data !== null &&
        typeof config.data === "object" &&
        !(config.data instanceof FormData) &&
        !(config.data instanceof URLSearchParams)
      ) {
        config.data = bodyStr;
      }
      config.headers["X-Nonce"] = nonce;
      config.headers["X-Timestamp"] = timestamp;
      config.headers["X-Sign"] = sign;
    } else if (
      token &&
      isFormDataUpload(config.data) &&
      !shouldSkipSign(requestUrl)
    ) {
      ElMessage.error("登录凭证不完整，请重新登录后再上传");
      return Promise.reject(new Error("缺少签名凭证"));
    }

    return config;
  },
  error => {
    ElMessage.error(error.data.error.message);
    return Promise.reject(error.data.error.message);
  }
);

instance.interceptors.response.use(
  function(config) {
    removePending(config.config);
    const responseType = config.config?.responseType;
    if (responseType === "arraybuffer" || responseType === "blob") {
      return Promise.resolve(config);
    }

    const rawStatus =
      config?.data?.status ??
      config?.data?.data?.status ??
      config?.data?.data?.data?.status;
    const rawMessage =
      config?.data?.message ??
      config?.data?.data?.message ??
      config?.data?.data?.data?.message;
    const status = Number(rawStatus);
    const requestUrl = config.config?.url as string | undefined;

    if (config.data === null || config.data === "") {
      return Promise.reject({
        ...config,
        message: "接口返回空数据，请稍后重试"
      });
    }

    if (status === 200 || status === 204) {
      return Promise.resolve(config);
    }
    if (isSignatureErrorStatus(status)) {
      handleSignatureError(status, rawMessage);
      return Promise.reject(config);
    }
    if (status === 401 || status === 397 || status === 403) {
      showAuthFailToastOnce(rawMessage, status);
      if (!shouldSkipAuthRedirect(requestUrl)) {
        redirectToLogin();
      }
      return Promise.reject(config);
    } else {
      ElMessage.error(rawMessage || "请求失败");
      return Promise.reject(config);
    }
  },
  function(error) {
    const { response } = error;
    if (response) {
      const { status: bizStatus, message: bizMessage } =
        extractBusinessResponseMeta(response.data);

      if (isSignatureErrorStatus(bizStatus)) {
        handleSignatureError(bizStatus, bizMessage);
        return Promise.reject(response);
      }

      errorHandle(
        response.status,
        bizMessage || response.data?.message || response.data?.msg
      );

      const config = error.config;
      const [RETRY_COUNT, RETRY_DELAY] = [3, 1000];

      if (config && shouldRetryRequest(config, error) && RETRY_COUNT) {
        config.__retryCount = config.__retryCount || 0;
        if (config.__retryCount >= RETRY_COUNT) {
          return Promise.reject(response || { message: error.message });
        }
        config.__retryCount++;
        const backoff = new Promise<void>(resolve => {
          setTimeout(() => {
            resolve();
          }, RETRY_DELAY || 1);
        });
        return backoff.then(() => {
          return instance(config);
        });
      }

      return Promise.reject(response);
    } else {
      return Promise.reject(error);
    }
  }
);

export default instance;
